import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  GraduationCap, Users, Search, Building2, ChevronLeft, 
  CheckCircle, AlertCircle, Clock, XCircle, Loader2,
  Filter, Atom
} from 'lucide-react';
import { toast } from 'sonner';

// NDT Methods with labels
const NDT_METHOD_LABELS = {
  'AE': 'AE (Acoustic Emission)',
  'ET': 'ET (Eddy Current)',
  'LT': 'LT (Leak Testing)',
  'MT': 'MT (Magnetic Particle)',
  'PCT': 'PCT (Pressure Change Testing)',
  'PT': 'PT (Liquid Penetrant)',
  'RT': 'RT (Radiographic)',
  'RVI': 'RVI (Remote Visual Inspection)',
  'UT': 'UT (Ultrasonic)',
  'UTT': 'UTT (Ultrasonic Thickness)',
  'VTG': 'VTG (Visual Testing General)',
  'VTW': 'VTW (Visual Testing Welds)'
};

// Status color mapping
const STATUS_COLORS = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400'
};

// Status badge styles
const STATUS_BADGE_STYLES = {
  PENDING: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-700',
  EXPIRING: 'bg-amber-100 text-amber-800',
  EXPIRED: 'bg-red-100 text-red-700',
  SUSPENDED: 'bg-yellow-100 text-yellow-700',
  REVOKED: 'bg-red-100 text-red-800'
};

// Phase 1 NDT Level enum — must match backend NDT_LEVEL_VALUES.
const NDT_LEVEL_OPTIONS = ['Trainee', 'Level I', 'Level II', 'Level III'];

// Role display labels
const ROLE_LABELS = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  OPERATOR: 'Operator',
  TECHNICIAN: 'Technician'
};

// Methods list + detail page render acronym-only. VTG and VTW are tracked
// distinctly (system-wide rename); we no longer collapse them to "VT".
const NDT_METHOD_ACRONYMS = {
  AE: 'AE', ET: 'ET', LT: 'LT', MT: 'MT', PCT: 'PCT', PT: 'PT',
  RT: 'RT', RVI: 'RVI', UT: 'UT', UTT: 'UTT', VTG: 'VTG', VTW: 'VTW',
};

// Render a stored date string as MM-DD-YYYY without any timezone-shift quirks.
// Accepts either YYYY-MM-DD or an ISO timestamp like 2026-06-01T00:00:00+00:00.
const formatMMDDYYYY = (value) => {
  if (!value) return '-';
  const dateOnly = String(value).split('T')[0];
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return dateOnly;
  return `${m[2]}-${m[3]}-${m[1]}`;
};

// Status indicator dot component
function StatusDot({ color }) {
  return (
    <span 
      className={`inline-block w-3 h-3 rounded-full ${STATUS_COLORS[color] || STATUS_COLORS.gray}`}
      title={color}
    />
  );
}

// Status badge component
function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_BADGE_STYLES[status] || STATUS_BADGE_STYLES.PENDING}`}>
      {status}
    </span>
  );
}

// Personnel List Component (Phase 1: still Technicians-only; Phase 2 will broaden roles)
function TechnicianList({ technicians, onSelectTechnician, selectedFacility, facilities, onFacilityChange, loading, userRole }) {
  const [searchTerm, setSearchTerm] = useState('');
  // 2026-07-19 Phase 1 — Operators/Technicians must never see or select
  // "All Facilities". Admins & Managers may still pick "All" (their scoped
  // set) when their access covers more than one facility.
  const canPickAllFacilities = userRole === 'ADMIN' || userRole === 'MANAGER';
  
  const filteredTechnicians = technicians.filter(tech => {
    const matchesSearch = 
      tech.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.technician_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="personnel-search"
          />
        </div>
        {facilities.length > 1 && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select 
              value={selectedFacility}
              onChange={(e) => onFacilityChange(e.target.value)}
              className="h-10 px-3 border rounded-md text-sm"
              data-testid="facility-filter"
            >
              {canPickAllFacilities && <option value="">All Facilities</option>}
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Personnel Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
        </div>
      ) : filteredTechnicians.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No personnel found</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full" data-testid="personnel-list-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facility</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NDT Methods</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status Summary</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTechnicians.map(tech => {
                const roleLabel = ROLE_LABELS[tech.role] || ROLE_LABELS.TECHNICIAN;
                const isTech = (tech.role || 'TECHNICIAN') === 'TECHNICIAN';
                return (
                  <tr 
                    key={tech.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onSelectTechnician(tech)}
                    data-testid={`personnel-row-${tech.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{tech.first_name} {tech.last_name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-testid={`personnel-role-${tech.id}`}>{roleLabel}</td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-testid={`personnel-id-${tech.id}`}>
                      {isTech ? (tech.technician_id || '-') : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tech.facilities?.map(f => f.name).join(', ') || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{tech.ndt_methods_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-xs" data-testid={`personnel-status-summary-${tech.id}`}>
                        {tech.ndt_status_summary?.ACTIVE > 0 && (
                          <span className="flex items-center gap-1 text-green-600" title="Active">
                            <CheckCircle className="w-3 h-3" />
                            {tech.ndt_status_summary.ACTIVE}
                          </span>
                        )}
                        {tech.ndt_status_summary?.EXPIRING > 0 && (
                          <span className="flex items-center gap-1 text-amber-600" title="Expiring within 90 days">
                            <Clock className="w-3 h-3" />
                            {tech.ndt_status_summary.EXPIRING}
                          </span>
                        )}
                        {tech.ndt_status_summary?.PENDING > 0 && (
                          <span className="flex items-center gap-1 text-gray-500" title="Pending">
                            <Clock className="w-3 h-3" />
                            {tech.ndt_status_summary.PENDING}
                          </span>
                        )}
                        {tech.ndt_status_summary?.EXPIRED > 0 && (
                          <span className="flex items-center gap-1 text-red-600" title="Expired">
                            <AlertCircle className="w-3 h-3" />
                            {tech.ndt_status_summary.EXPIRED}
                          </span>
                        )}
                        {(tech.ndt_status_summary?.SUSPENDED > 0 || tech.ndt_status_summary?.REVOKED > 0) && (
                          <span className="flex items-center gap-1 text-yellow-600" title="Suspended / Revoked">
                            <XCircle className="w-3 h-3" />
                            {(tech.ndt_status_summary?.SUSPENDED || 0) + (tech.ndt_status_summary?.REVOKED || 0)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// NDT Methods List Component (read-only summary; all editing happens on Method Detail Page)
function NdtRecordsView({ technician, onBack, api }) {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/lms/technicians/${technician.id}/ndt-records?include_inactive=${showInactive}`);
      setRecords(response.data.ndt_records || []);
    } catch (error) {
      toast.error('Failed to load NDT records');
    } finally {
      setLoading(false);
    }
  }, [api, technician.id, showInactive]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const activeRecords = records.filter(r => r.active_flag !== false);
  const inactiveRecords = records.filter(r => r.active_flag === false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Personnel List
          </Button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {technician.first_name} {technician.last_name}
            </h2>
            <p className="text-sm text-gray-500">
              {technician.technician_id ? `ID: ${technician.technician_id}` : 'No ID assigned'} • {technician.email}
            </p>
          </div>
        </div>
      </div>

      {/* NDT Methods List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
        </div>
      ) : activeRecords.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500">No NDT methods enabled for this technician.</p>
            <p className="text-sm text-gray-400 mt-1">
              Enable NDT methods in Technician Qualifications to start tracking training.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-[#005EF8]" />
              NDT Methods List
              <span className="text-xs bg-[#005EF8]/10 text-[#005EF8] px-2 py-0.5 rounded-full">
                {activeRecords.length} active
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[900px]" data-testid="ndt-records-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[80px]">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facility</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certification Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[150px]">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {activeRecords.map(record => (
                    <tr
                      key={record.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/training/ndt/methods/${record.id}?personnelId=${technician.id}`)}
                      data-testid={`ndt-record-${record.id}`}
                    >
                      <td className="px-4 py-3">
                        <span
                          className="font-medium text-gray-900"
                          data-testid={`ndt-method-link-${record.id}`}
                        >
                          {NDT_METHOD_ACRONYMS[record.method] || record.method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.facility_name}</td>
                      <td className="px-4 py-3 text-sm">{record.level || '-'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{formatMMDDYYYY(record.certification_date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatMMDDYYYY(record.due_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StatusDot color={record.status_color} />
                          <StatusBadge status={record.status} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Inactive records toggle */}
            {inactiveRecords.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <button
                  onClick={() => setShowInactive(!showInactive)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {showInactive ? 'Hide' : 'Show'} {inactiveRecords.length} inactive method(s)
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Main Learning Management Page
export default function LearningManagementPage() {
  const { api, hasRole, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [technicians, setTechnicians] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch facilities for filter
      const facilitiesRes = await api.get('/lms/facilities');
      setFacilities(facilitiesRes.data || []);

      // Fetch technicians
      const params = selectedFacility ? `?facility_id=${selectedFacility}` : '';
      const techRes = await api.get(`/lms/technicians${params}`);
      setTechnicians(techRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-select a technician when ?personnelId=… is in the URL (e.g. on back-nav from a Method Detail Page).
  useEffect(() => {
    const pid = searchParams.get('personnelId');
    if (pid && technicians.length > 0 && (!selectedTechnician || selectedTechnician.id !== pid)) {
      const match = technicians.find(t => t.id === pid);
      if (match) setSelectedTechnician(match);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, technicians]);

  const handleSelectTechnician = (tech) => {
    setSelectedTechnician(tech);
  };

  const handleBackToList = () => {
    setSelectedTechnician(null);
    // Drop the personnelId so we don't auto-reopen the technician on next nav.
    if (searchParams.get('personnelId')) {
      searchParams.delete('personnelId');
      setSearchParams(searchParams);
    }
    fetchData();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ndt-personnel-page">
      {/* Page Header */}
      {!selectedTechnician && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#005EF8]/10 rounded-lg">
              <Atom className="w-6 h-6 text-[#005EF8]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">NDT Personnel</h1>
              <p className="text-sm text-gray-500">Track and manage NDT training certifications</p>
            </div>
          </div>
          {user?.role === 'ADMIN' && (
            <Link to="/training/ndt/hours-targets">
              <Button variant="outline" size="sm" data-testid="open-hours-targets-btn">
                <Clock className="w-4 h-4 mr-2" />
                Trainee Hours Targets
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Content */}
      {selectedTechnician ? (
        <NdtRecordsView 
          technician={selectedTechnician}
          onBack={handleBackToList}
          api={api}
        />
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-[#005EF8]" />
              Personnel
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full" data-testid="personnel-total-badge">
                {technicians.length} total
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TechnicianList 
              technicians={technicians}
              onSelectTechnician={handleSelectTechnician}
              selectedFacility={selectedFacility}
              facilities={facilities}
              onFacilityChange={setSelectedFacility}
              loading={loading}
              userRole={user?.role}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
